import { watch } from 'vue';
import { useToast } from 'vue-toast-notification';

import moment from 'moment-timezone';

import { ModalName } from '@/constants/modals';
import { getMissionStatusInfo } from '@/helpers/mission';
import { formatFullName } from '@/helpers/name';
import i18n from '@/i18n';
import { useCompanyNeedOptimistic } from '@/modules/companyNeed/composables/useCompanyNeedOptimistic';
import { openProfileRejectionModal } from '@/modules/companyNeed/helpers/reject';
import { MoveWorkerCompanyNeedCommands } from '@/modules/companyNeed/types/moveWorker';

import type { RejectionSuccessPayload } from '@/components/modal/Profile/rejectProfile/types';
import type { AvatarProps } from '@/dsComponents/Avatar/types';
import type { TableMassAction } from '@/dsComponents/Table/types';
import type { OptimisticRejectionWorker } from '@/modules/companyNeed/helpers/rejectOptimistic';
import type { CompanyNeedQuickActionResponse } from '@/modules/companyNeed/types';
import type {
	CompanyNeedItem,
	CompanyNeedModal,
} from '@/modules/companyNeed/types/modals';
import type {
	MissionCommand,
	MoveActionCommand,
	MoveWorker,
	MoveWorkerActionItem,
	MoveWorkerCompanyNeedModalProps,
} from '@/modules/companyNeed/types/moveWorker';
import type { FormattedData as CandidatesFormattedData } from '@/modules/companyNeed/views/details/candidates/types';
import type { FormattedData as MatchingFormattedData } from '@/modules/companyNeed/views/details/matching/types';
import type {
	MissionList,
	MissionPeriod,
} from '@/modules/companyNeed/views/details/missions/types';
import type { FormattedData as PoolFormattedData } from '@/modules/companyNeed/views/details/pool/types';

import { DropdownItemColors } from '@/dsComponents/Dropdown/types';
import { useModalStore } from '@/dsComponents/modals/modalStore';

type MoveSelectionItem =
	| MatchingFormattedData
	| PoolFormattedData
	| CandidatesFormattedData;

const isMatchingItem = (
	item: MoveSelectionItem
): item is MatchingFormattedData => 'avatarProps' in item.profile;

const getSelectionAvatarProps = (item: MoveSelectionItem): AvatarProps =>
	isMatchingItem(item) ? item.profile.avatarProps : item.profile;

const getSelectionWorkerId = (item: MoveSelectionItem): string =>
	'workerId' in item ? item.workerId : item.id;

export const openMoveWorkerModal = (
	props: MoveWorkerCompanyNeedModalProps
): Promise<void> => {
	const modalStore = useModalStore();
	modalStore.actions.open(ModalName.MoveWorkerCompanyNeedModal, props);

	return new Promise((resolve) => {
		const unwatch = watch(
			() =>
				modalStore.state.open.includes(ModalName.MoveWorkerCompanyNeedModal),
			(isOpen) => {
				if (!isOpen) {
					unwatch();
					resolve();
				}
			}
		);
	});
};

export const getMoveWorkerActionItems = (
	worker: MoveWorker,
	companyNeedId: string,
	options: {
		includeRefuse?: boolean;
		isMissionProposed?: boolean;
		workerId?: string;
		companyId?: string;
		onViewDetails?: (worker: MoveWorker) => void;
		onSaveLastContact?: () => void;
		onScheduleInterview?: () => void;
		onEditInterview?: () => void;
		onEditInterviews?: { label: string; onClick: () => void }[];
		onRemoveFromPool?: (workerId: string) => void;
		onMoveSuccess?: (
			command?: MoveWorkerCompanyNeedCommands
		) => void | Promise<void>;
		isFromMatching?: boolean;
		isFromRecruitment?: boolean;
		fromJobOffer?: boolean;
	} = { isFromMatching: false, isFromRecruitment: false }
): MoveWorkerActionItem[] => {
	const toaster = useToast();
	const handleViewDetails = options.onViewDetails ?? (() => {});
	const actions: MoveWorkerActionItem[] = [
		{
			label: i18n.global.t('page.companyNeed.details.table.actions.details'),
			iconLeft: 'search',
			onClick: () => handleViewDetails(worker),
		},
		{
			label: i18n.global.t('global.actions.move'),
			iconLeft: 'arrow-right',
			onClick: () => {
				const resolvedWorker = options.workerId
					? { ...worker, id: options.workerId }
					: worker;
				openMoveWorkerModal({
					open: true,
					workers: [resolvedWorker],
					companyNeedId,
					companyId: options.companyId ?? '',
					isFromMatching: options.isFromMatching,
					isFromRecruitment: options.isFromRecruitment,
					onSuccess: options.onMoveSuccess,
				});
			},
		},
	];

	if (options.onSaveLastContact) {
		const handleSaveLastContact = options.onSaveLastContact;
		actions.push({
			label: i18n.global.t(
				'page.companyNeed.details.table.actions.saveLastContact'
			),
			iconLeft: 'phone-arrow-left',
			onClick: () => handleSaveLastContact(),
		});
	}

	if (options.onEditInterviews?.length) {
		actions.push({
			label: i18n.global.t(
				'page.companyNeed.details.table.actions.editOneInterview'
			),
			iconLeft: 'cal-edit',
			hasSubmenu: true,
			submenuItems: options.onEditInterviews,
		});
	} else if (options.onEditInterview) {
		const handleEditInterview = options.onEditInterview;
		actions.push({
			label: i18n.global.t(
				'page.companyNeed.details.table.actions.editInterview'
			),
			iconLeft: 'cal-edit',
			onClick: () => handleEditInterview(),
		});
	}

	if (options.onScheduleInterview) {
		const handleScheduleInterview = options.onScheduleInterview;
		actions.push({
			label: i18n.global.t(
				'page.companyNeed.details.table.actions.addInterview'
			),
			iconLeft: 'cal-plus',
			onClick: () => handleScheduleInterview(),
		});
	}

	if (options.onRemoveFromPool) {
		const handleRemoveFromPool = options.onRemoveFromPool;
		const resolvedWorkerId = options.workerId ?? worker.id;
		actions.push({
			label: i18n.global.t(
				'page.companyNeed.details.table.actions.removeFromPool'
			),
			iconLeft: 'star-x',
			iconLeftColor: DropdownItemColors.red,
			labelColor: DropdownItemColors.red,
			onClick: () => handleRemoveFromPool(resolvedWorkerId),
		});
	}

	if (options.includeRefuse !== false) {
		const workerId = options.workerId ?? worker.id;
		if (!workerId) {
			return actions;
		}
		const {
			handleRejectionFromMatching,
			handleRejectionFromRecruitment,
			handleRejectionFromCandidates,
			handleRemoveFromAgency,
		} = useCompanyNeedOptimistic();
		actions.push({
			label: i18n.global.t('global.actions.refuse'),
			iconLeft: 'x',
			iconLeftColor: DropdownItemColors.red,
			labelColor: DropdownItemColors.red,
			onClick: () => {
				openProfileRejectionModal(
					[workerId],
					companyNeedId,
					true,
					(payload: RejectionSuccessPayload) => {
						const optimisticWorker: OptimisticRejectionWorker = {
							id: workerId,
							firstname: worker.firstname,
							lastname: worker.lastname,
							picture: worker.picture,
						};
						if (options.isFromRecruitment) {
							handleRejectionFromRecruitment([optimisticWorker], payload);
						} else if (options.fromJobOffer) {
							handleRejectionFromCandidates([optimisticWorker], payload);
						} else if (options.isFromMatching) {
							handleRejectionFromMatching([optimisticWorker], payload);
						}
						toaster.open({
							message: i18n.global.t(
								'modals.companyNeed.moveWorker.refuseWorkers.success',
								{ count: payload.successCount }
							),
							type: 'success',
							duration: 3000,
						});
					},
					options.isMissionProposed ?? false,
					options.fromJobOffer ?? false,
					formatFullName(worker),
					async (workerIds) => {
						handleRemoveFromAgency(
							workerIds,
							options.isFromRecruitment ? ['recruitment'] : []
						);
					}
				);
			},
		});
	}

	return actions;
};

export const moveModalCommandsMap: Record<string, MoveActionCommand> = {
	[MoveWorkerCompanyNeedCommands.MOVE_TO_PREQUALIFIED]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_PREQUALIFIED,
		icon: 'status-marker',
		label: i18n.global.t('modals.companyNeed.moveWorker.prequalified'),
		iconColor: 'var(--icon-sub-500, #636778)',
	},
	[MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED,
		icon: 'status-marker',
		label: i18n.global.t('modals.companyNeed.moveWorker.contacted'),
		iconColor: 'var(--yellow-base, #FAB305)',
	},
	[MoveWorkerCompanyNeedCommands.MOVE_TO_INTERVIEW]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_INTERVIEW,
		icon: 'status-marker',
		label: i18n.global.t('modals.companyNeed.moveWorker.interview'),
		iconColor: 'var(--orange-base, #FF5006)',
	},
	[MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_PROPOSED]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_PROPOSED,
		icon: 'status-marker',
		label: i18n.global.t('modals.companyNeed.moveWorker.proposedToCompany'),
		iconColor: 'var(--blue-base, #2051F8)',
	},
	[MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_INTERVIEW]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_INTERVIEW,
		icon: 'status-marker',
		label: i18n.global.t('modals.companyNeed.moveWorker.companyInterview'),
		iconColor: 'var(--purple-base, #7520E6)',
	},
	[MoveWorkerCompanyNeedCommands.MOVE_TO_POOL]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_POOL,
		icon: 'star',
		label: i18n.global.t('modals.companyNeed.moveWorker.addToPool'),
		iconColor: 'var(--icon-sub-500, #636778)',
	},
	[MoveWorkerCompanyNeedCommands.ADD_TO_ANOTHER_MISSION]: {
		key: MoveWorkerCompanyNeedCommands.ADD_TO_ANOTHER_MISSION,
		icon: 'order-arrow-left',
		label: i18n.global.t('modals.companyNeed.moveWorker.addToExistingMission'),
		iconColor: 'var(--icon-sub-500, #636778)',
	},
	[MoveWorkerCompanyNeedCommands.CREATE_NEW_MISSION_WITH_THIS_WORKER]: {
		key: MoveWorkerCompanyNeedCommands.CREATE_NEW_MISSION_WITH_THIS_WORKER,
		icon: 'plus',
		label: i18n.global.t(
			'modals.companyNeed.moveWorker.createNewMissionFromProfiles'
		),
		iconColor: 'var(--icon-sub-500, #636778)',
	},
	[MoveWorkerCompanyNeedCommands.ADD_TO_OTHER_POOLS]: {
		key: MoveWorkerCompanyNeedCommands.ADD_TO_OTHER_POOLS,
		icon: 'star',
		label: i18n.global.t(
			'modals.companyNeed.moveWorker.addToOtherPools.action'
		),
		iconColor: 'var(--icon-sub-500, #636778)',
	},
	[MoveWorkerCompanyNeedCommands.MOVE_TO_ANOTHER_COMPANY_NEED]: {
		key: MoveWorkerCompanyNeedCommands.MOVE_TO_ANOTHER_COMPANY_NEED,
		icon: 'folder-open',
		label: i18n.global.t('modals.companyNeed.moveWorker.moveToAnotherNeed'),
		iconColor: 'var(--icon-sub-500, #636778)',
	},
};

const formatMissionDate = (date: number): string =>
	moment(date).locale('fr').format('dd DD MMM');

const getDateRange = (periods: MissionPeriod[]) => {
	if (!periods.length) return { startDate: null, endDate: null };
	return {
		startDate: Math.min(
			...periods.map((period) => moment(period.start).valueOf())
		),
		endDate: Math.max(...periods.map((period) => moment(period.end).valueOf())),
	};
};

export const formatMissionsToSections = (
	missions: MissionList[]
): MissionCommand[] =>
	missions.map((mission) => {
		const { icon, iconColor } = getMissionStatusInfo(mission.status);
		const fill =
			mission.totalPositions > 0
				? Math.round((mission.confirmedWorkers / mission.totalPositions) * 100)
				: 0;
		const { startDate, endDate } = getDateRange(mission.periods);

		return {
			key: mission.id,
			label: mission.name,
			icon,
			iconColor,
			startDate: startDate !== null ? formatMissionDate(startDate) : undefined,
			endDate: endDate !== null ? formatMissionDate(endDate) : undefined,
			fill,
			progressLabel: `${mission.confirmedWorkers}/${mission.totalPositions}`,
		};
	});

export const getMoveMultiSelectionMassActions = (
	companyNeedId: string,
	options: {
		includeRefuse?: boolean;
		companyId?: string;
		isFromMatching?: boolean;
		fromJobOffer?: boolean;
		onRemoveFromPool?: (workerIds: string[]) => void;
	} = {
		isFromMatching: false,
	}
): TableMassAction[] => {
	const toaster = useToast();
	const actions: TableMassAction[] = [
		{
			button: {
				label: i18n.global.t('global.actions.move'),
				variant: 'stroke',
				type: 'neutral',
				size: 'sm',
				leftIcon: 'arrow-right',
			},
			onClick: (selectedItems: MoveSelectionItem[]) =>
				openMoveWorkerModal({
					open: true,
					workers: selectedItems.map((item) => {
						const matching = isMatchingItem(item);
						const avatarProps = getSelectionAvatarProps(item);
						return {
							id: getSelectionWorkerId(item),
							firstname: avatarProps.firstname || '',
							lastname: avatarProps.lastname || '',
							phone: item.phone.cellTitle,
							email: item.email,
							picture: avatarProps.imageUrl ?? undefined,
							fromJobOffer: matching
								? item.jobOfferResponse !== null
								: (options.fromJobOffer ?? false),
							availableCommands: item.availableCommands || [],
							history: 'history' in item ? item.history || [] : [],
						};
					}),
					companyNeedId,
					companyId: options.companyId ?? '',
					isFromMatching: options.isFromMatching,
				}),
		},
	];

	if (options.onRemoveFromPool) {
		const handleRemoveFromPool = options.onRemoveFromPool;
		actions.push({
			button: {
				label: i18n.global.t(
					'page.companyNeed.details.table.actions.removeFromPool'
				),
				variant: 'stroke',
				type: 'error',
				size: 'sm',
				leftIcon: 'star-x',
			},
			onClick: (selectedItems: MoveSelectionItem[]) => {
				const workerIds = selectedItems.map((item) =>
					getSelectionWorkerId(item)
				);
				handleRemoveFromPool(workerIds);
			},
		});
	}

	if (options.includeRefuse !== false) {
		const {
			handleRejectionFromMatching,
			handleRejectionFromCandidates,
			handleRemoveFromAgency,
		} = useCompanyNeedOptimistic();
		actions.push({
			button: {
				label: i18n.global.t('global.actions.refuse'),
				variant: 'stroke',
				type: 'error',
				size: 'sm',
				leftIcon: 'x',
			},
			onClick: (selectedItems: MoveSelectionItem[]) => {
				const optimisticWorkers: OptimisticRejectionWorker[] =
					selectedItems.map((item) => {
						const avatarProps = getSelectionAvatarProps(item);
						return {
							id: getSelectionWorkerId(item),
							firstname: avatarProps.firstname || '',
							lastname: avatarProps.lastname || '',
							picture: avatarProps.imageUrl ?? undefined,
						};
					});
				const workerName =
					optimisticWorkers.length === 1
						? formatFullName({
								firstname: optimisticWorkers[0].firstname,
								lastname: optimisticWorkers[0].lastname,
							})
						: undefined;
				openProfileRejectionModal(
					optimisticWorkers.map((worker) => worker.id),
					companyNeedId,
					true,
					(payload: RejectionSuccessPayload) => {
						if (options.fromJobOffer) {
							handleRejectionFromCandidates(optimisticWorkers, payload);
						} else if (options.isFromMatching) {
							handleRejectionFromMatching(optimisticWorkers, payload);
						}
						toaster.open({
							message: i18n.global.t(
								'modals.companyNeed.moveWorker.refuseWorkers.success',
								{ count: payload.successCount }
							),
							type: 'success',
							duration: 3000,
						});
					},
					false,
					options.fromJobOffer ?? false,
					workerName,
					async (workerIds) => {
						handleRemoveFromAgency(workerIds);
					}
				);
			},
		});
	}

	return actions;
};

export const normalizeString = (str: string): string =>
	str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();

export const mapCompanyNeedsQuickActionToModalItems = (
	response: CompanyNeedQuickActionResponse[]
): CompanyNeedModal[] => {
	const groupedByCompany = response.reduce<
		Map<string, CompanyNeedQuickActionResponse[]>
	>((acc, item) => {
		const existing = acc.get(item.companyId) || [];
		existing.push(item);
		acc.set(item.companyId, existing);
		return acc;
	}, new Map());

	return Array.from(groupedByCompany.entries()).map(([companyId, needs]) => {
		const firstNeed = needs[0];
		return {
			companyId,
			companyName: firstNeed.companyName,
			needs: needs.map(
				(need): CompanyNeedItem => ({
					id: need.id,
					name: need.name,
					job: need.jobName ?? '',
					orderCount: need.numberMissionsLength ?? 0,
					poolCount: need.poolProfilesLength ?? 0,
				})
			),
		};
	});
};
